import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, getAuthToken } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Store, Plus, X, Building2, Sparkles, Check } from "lucide-react";

const MAX_KIOSKS = 8;

export default function Onboarding() {
  const nav = useNavigate();
  const [company, setCompany] = useState("");
  const [vat, setVat] = useState("");
  const [kiosks, setKiosks] = useState([""]);
  const [defaultFloat, setDefaultFloat] = useState("100");
  const [language, setLanguage] = useState("both");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  // If they hit /onboarding while not logged in → bounce them
  useEffect(() => {
    if (!getAuthToken()) {
      nav("/register", { replace: true });
      return;
    }
    api.get("/auth/me")
      .then(({ data }) => {
        if (data.tenant?.onboarding_complete) {
          nav("/login", { replace: true });
          return;
        }
        setCompany(data.tenant?.company_name || "");
      })
      .catch(() => nav("/saas-login", { replace: true }))
      .finally(() => setLoading(false));
  }, [nav]);

  const addKiosk = () => kiosks.length < MAX_KIOSKS && setKiosks([...kiosks, ""]);
  const removeKiosk = (i) => setKiosks(kiosks.filter((_, idx) => idx !== i));
  const setKiosk = (i, v) => setKiosks(kiosks.map((k, idx) => (idx === i ? v : k)));

  const submit = async () => {
    if (!company.trim()) {
      toast.error("Company name is required");
      return;
    }
    const cleanKiosks = kiosks.map((k) => k.trim()).filter(Boolean);
    if (cleanKiosks.length === 0) {
      toast.error("Add at least one kiosk");
      return;
    }
    const f = defaultFloat === "" ? 0 : Number(defaultFloat);
    setBusy(true);
    try {
      await api.post("/auth/onboarding", {
        company_name: company.trim(),
        vat_number: vat.trim() || null,
        kiosk_names: cleanKiosks,
        default_float: f,
        language,
      });
      toast.success("Setup complete · Καλωσήρθες!");
      nav("/login", { replace: true });
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      toast.error(typeof msg === "string" ? msg : "Setup failed");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500">
        Loading…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white p-6 sm:p-10" data-testid="onboarding-page">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-3 mb-8">
          <div className="h-12 w-12 rounded-xl bg-white text-slate-900 flex items-center justify-center">
            <Sparkles className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Onboarding · Παραμετροποίηση</h1>
            <p className="text-slate-400 text-sm">Step 1 of 1 · ~ 2 minutes</p>
          </div>
        </div>

        <Card className="p-6 bg-white text-slate-900 space-y-5">
          {/* Company + VAT */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1">
                <Building2 className="h-3 w-3" /> Company Name · Επωνυμία
              </Label>
              <Input
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                placeholder="e.g. My Kiosks Ltd"
                data-testid="onboarding-company"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-500">
                VAT Registration Number · Α.Φ.Μ.
              </Label>
              <Input
                value={vat}
                onChange={(e) => setVat(e.target.value)}
                placeholder="optional · e.g. CY10123456A"
                data-testid="onboarding-vat"
                className="mt-1 font-mono"
              />
            </div>
          </div>

          {/* Kiosk list */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1">
                <Store className="h-3 w-3" /> Kiosk Names · Καταστήματα ({kiosks.length}/{MAX_KIOSKS})
              </Label>
              <Button
                size="sm"
                variant="outline"
                onClick={addKiosk}
                disabled={kiosks.length >= MAX_KIOSKS}
                className="h-7 gap-1"
                data-testid="onboarding-add-kiosk"
              >
                <Plus className="h-3 w-3" />
                <span className="text-[11px]">Add Kiosk</span>
              </Button>
            </div>
            <div className="space-y-2">
              {kiosks.map((k, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`onboarding-kiosk-row-${i}`}>
                  <span className="text-xs text-slate-400 w-5">{i + 1}.</span>
                  <Input
                    value={k}
                    onChange={(e) => setKiosk(i, e.target.value)}
                    placeholder={`Kiosk ${i + 1} name`}
                    data-testid={`onboarding-kiosk-${i}`}
                  />
                  {kiosks.length > 1 && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => removeKiosk(i)}
                      className="h-8 w-8 p-0 text-slate-400 hover:text-red-600"
                      data-testid={`onboarding-remove-kiosk-${i}`}
                      title="Remove"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Float + language */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-500">
                Default Starting Float (€) · Αρχικό Ταμείο
              </Label>
              <Input
                type="number"
                step="0.01"
                value={defaultFloat}
                onChange={(e) => setDefaultFloat(e.target.value)}
                data-testid="onboarding-default-float"
                className="mt-1 font-mono"
              />
              <p className="text-[10px] text-slate-500 mt-1">
                Applied to every kiosk when a shift starts. Override per shift if needed.
              </p>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-500">
                Language · Γλώσσα
              </Label>
              <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger className="mt-1" data-testid="onboarding-language">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="both">Bilingual EN + EL</SelectItem>
                  <SelectItem value="en">English only</SelectItem>
                  <SelectItem value="el">Ελληνικά μόνο</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <Button
            onClick={submit}
            disabled={busy}
            className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white font-bold gap-2"
            data-testid="onboarding-submit"
          >
            <Check className="h-4 w-4" />
            {busy ? "Setting up…" : "Complete Setup · Ολοκλήρωση"}
          </Button>
        </Card>

        <p className="text-[11px] text-slate-400 mt-6 text-center">
          You are on a 14-day free trial · €20 / month after · Cancel anytime
        </p>
      </div>
    </div>
  );
}
